import { GridColDef } from "@mui/x-data-grid";
import { formatDateAndTime } from "../../../../helpers/appHelpers";
import {
    AuditLogActor,
    AuditLogError,
    AuditLogJson,
    AuditLogOutcome,
} from "../../../../models/audit-log.dto";

/** Renders a JSON value as text, never as a raw object */
export const jsonToText = (value: AuditLogJson | undefined): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    return JSON.stringify(value);
};

/** A short, readable message for a recorded error envelope */
export const getAuditLogErrorMessage = (error: AuditLogError | null | undefined) =>
    jsonToText(error?.message ?? error?.error) || "--";

export const getAuditLogUserName = (user: AuditLogActor | null | undefined) =>
    user?.name || user?.username || "--";

const OUTCOME_DISPLAY: Record<AuditLogOutcome, { label: string; color: string }> = {
    [AuditLogOutcome.SUCCESSFUL]: { label: "Successful", color: "#4CAF50" },
    [AuditLogOutcome.FAILED]: { label: "Failed", color: "#F44336" },
};

export const AuditLogOutcomeChip = ({ outcome }: { outcome: AuditLogOutcome }) => {
    const display = OUTCOME_DISPLAY[outcome] || { label: outcome || "--", color: "#808080" };

    return (
        <div
            className="rounded-lg px-4 py-2 min-w-[80px] w-fit text-center relative font-semibold overflow-hidden"
            style={{ color: display.color }}
        >
            <div
                className="absolute inset-0"
                style={{ background: display.color, opacity: "15%" }}
            />
            <p>{display.label}</p>
        </div>
    );
};

/** Options for the table filter's outcome select */
export const AuditLogOutcomeOptions = [
    { id: AuditLogOutcome.SUCCESSFUL, name: "Successful" },
    { id: AuditLogOutcome.FAILED, name: "Failed" },
];

export const AuditLogColumns: GridColDef[] = [
    {
        field: "timestamp",
        headerName: "Timestamp",
        minWidth: 180,
        flex: 1,
        headerClassName: "super-app-theme--header",
        sortComparator: (date1, date2) => new Date(date1).getTime() - new Date(date2).getTime(),
        valueGetter: (params) => formatDateAndTime(params),
    },
    {
        field: "user",
        headerName: "User",
        minWidth: 180,
        flex: 1,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) => getAuditLogUserName(params.value),
    },
    {
        field: "action",
        headerName: "Action",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
    },
    {
        field: "outcome",
        headerName: "Outcome",
        minWidth: 140,
        headerClassName: "super-app-theme--header",
        renderCell: (params) => <AuditLogOutcomeChip outcome={params.value} />,
    },
    {
        field: "statusCode",
        headerName: "Status",
        minWidth: 90,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) => params.value ?? "--",
    },
    {
        field: "error",
        headerName: "Error",
        minWidth: 220,
        flex: 1,
        headerClassName: "super-app-theme--header",
        valueGetter: (params) => getAuditLogErrorMessage(params.value),
    },
];